/**
 * Translation between what a record-backed board is showing and what the rows
 * endpoint is asked, in both directions: board state becomes query parameters,
 * and the server's totals and value counts become the numbers the group headers
 * and filter chips print.
 *
 * Pure and synchronous on purpose. It is the one place that decides what a
 * number on a record board means, so it is the one place worth testing hard, and
 * nothing here may fall back to counting rows the client happens to hold.
 *
 * Two limits come from the API rather than from choice, and are handled here so
 * the board cannot promise something the server will refuse:
 *
 * - There is no negation. `in`, `contains`, `gte`, `lte`, `empty` and `notEmpty`
 *   are the operators, so the advanced builder's `is not` has no counterpart and
 *   a record board does not offer it.
 * - Filters on one field OR together, filters on different fields AND. Two
 *   `gte`/`lte` pairs on one field therefore AND, so a date selection has to be
 *   a single interval. The three buckets are contiguous, which makes every
 *   selection but "overdue or later" expressible; the board keeps date
 *   selections to one bucket so that case cannot arise.
 */
import { addDays, endOfMonth, startOfDay, subDays } from 'date-fns';

import type { TrackerRowsParams } from '@/entities/tracker';
import type { TrackerRowValueCount } from '@/entities/tracker';

import {
  DATE_BUCKETS,
  filterKindForColumnType,
  type DateBucket,
  type FilterColumnSpec,
  type FilterValueCounts,
} from './filtering';
import { WORKSPACE_FIELD, type RecordField } from './record-fields';
import type { BoardFilters } from './usePersistentFilters';

/** Whole-set counts as the endpoint returns them: field id → value → rows. */
export type ServerValueCounts = Record<string, TrackerRowValueCount[]>;

/** The endpoint accepts at most this many `countBy` fields per request. */
export const MAX_COUNT_BY = 8;

/**
 * The value the server reports for a row that has none. It arrives as `null` in
 * the counts and has to survive a round trip through a string-keyed map, so it
 * gets a sentinel rather than being dropped: "how many have no owner" is one of
 * the more useful numbers on the board.
 */
export const NO_VALUE = '__no_value__';

export interface RecordBoardState {
  search: string;
  filters: BoardFilters;
  /** Field id the board is grouped by, or null for one flat group. */
  groupBy: string | null;
  sortBy: string | null;
  sortOrder: 'asc' | 'desc';
}

export interface RowsQueryPlan {
  params: TrackerRowsParams;
  /**
   * Countable fields the request had no `countBy` slot left for. Their chips
   * render without a number rather than with one the server did not produce.
   */
  uncountedFieldIds: readonly string[];
}

/**
 * Board state as the rows endpoint reads it.
 *
 * `countBy` covers the grouping first, because the group headers cannot render
 * without it, then the filterable fields on show. Anything past the server's
 * limit is reported rather than dropped quietly.
 */
export function buildRowsQuery(
  state: RecordBoardState,
  fields: readonly RecordField[],
  visibleFieldIds: readonly string[],
  now: Date,
): RowsQueryPlan {
  const byId = new Map(fields.map((field) => [field.id, field]));

  const filter: string[] = [];
  for (const [fieldId, values] of Object.entries(state.filters)) {
    const field = byId.get(fieldId);
    if (!field || values.length === 0) continue;
    if (field.filter === 'in') {
      for (const value of values) filter.push(`${fieldId}:in:${value}`);
    } else if (field.filter === 'date') {
      const range = dateBucketRange(values[0] as DateBucket, now);
      if (range.gte) filter.push(`${fieldId}:gte:${range.gte}`);
      if (range.lte) filter.push(`${fieldId}:lte:${range.lte}`);
    }
  }

  const wanted: string[] = [];
  const push = (id: string) => {
    if (!wanted.includes(id) && byId.get(id)?.countable === true) wanted.push(id);
  };
  if (state.groupBy) push(state.groupBy);
  for (const id of visibleFieldIds) push(id);

  const sortField = state.sortBy ? byId.get(state.sortBy) : undefined;

  return {
    params: {
      ...(state.search.trim() ? { search: state.search.trim() } : {}),
      ...(sortField?.sortable === true ? { sortBy: sortField.id, sortOrder: state.sortOrder } : {}),
      ...(filter.length > 0 ? { filter } : {}),
      ...(wanted.length > 0 ? { countBy: wanted.slice(0, MAX_COUNT_BY) } : {}),
    },
    uncountedFieldIds: wanted.slice(MAX_COUNT_BY),
  };
}

/**
 * The `gte` / `lte` boundaries one date bucket means, against "now".
 *
 * Boundaries are whole days because the fields these narrow are dates a person
 * chose, and both ends are inclusive to match the operators: "overdue" ends the
 * day before today rather than at midnight, so a deadline of today is not
 * overdue by a few hours.
 */
export function dateBucketRange(bucket: DateBucket, now: Date): { gte?: string; lte?: string } {
  const today = startOfDay(now);
  const monthEnd = endOfMonth(now);
  switch (bucket) {
    case 'overdue':
      return { lte: isoDay(subDays(today, 1)) };
    case 'this-month':
      return { gte: isoDay(today), lte: isoDay(monthEnd) };
    case 'later':
      return { gte: isoDay(addDays(monthEnd, 1)) };
  }
}

/**
 * Filters trimmed to what the request can carry: only fields that still exist
 * and can be narrowed on, and a date column held to one bucket.
 *
 * Normalising here rather than at the request is what keeps the panel honest —
 * the chips render from this same state, so a selection shown as active is
 * always one the query applied.
 */
export function normaliseRecordFilters(
  filters: BoardFilters,
  fields: readonly RecordField[],
): BoardFilters {
  const byId = new Map(fields.map((field) => [field.id, field]));
  const entries = Object.entries(filters).flatMap(([fieldId, values]) => {
    const field = byId.get(fieldId);
    if (!field?.filter || values.length === 0) return [];
    if (field.filter === 'in') return [[fieldId, values] as const];
    const bucket = values.find((value): value is DateBucket =>
      (DATE_BUCKETS as readonly string[]).includes(value),
    );
    return bucket ? [[fieldId, [bucket]] as const] : [];
  });
  return Object.fromEntries(entries);
}

/**
 * The workspace selection narrowed to the workspaces the scope reads. A stored
 * pick can outlive a workspace leaving the scope, and sent as is it would empty
 * the board with no chip left to clear; with one workspace there is nothing to
 * pick between. The stored selection is not touched, so it applies again once
 * its workspaces come back.
 */
export function filtersWithinScope(
  filters: BoardFilters,
  resolvedWorkspaceIds: readonly string[] | undefined,
): BoardFilters {
  if (!(WORKSPACE_FIELD.id in filters)) return filters;
  const { [WORKSPACE_FIELD.id]: picked, ...rest } = filters;
  const readable = new Set(resolvedWorkspaceIds ?? []);
  if (readable.size <= 1) return rest;
  const kept = picked.filter((id) => readable.has(id));
  return kept.length > 0 ? { ...rest, [WORKSPACE_FIELD.id]: kept } : rest;
}

/**
 * The server's counts for one field as bucket id → rows, with rows that have no
 * value gathered under {@link NO_VALUE}.
 *
 * An absent field yields an empty map rather than zeroes: "the server did not
 * count this" and "the server counted none" are different answers, and only the
 * first one means a header should show nothing at all.
 */
export function countsForField(
  valueCounts: ServerValueCounts | undefined,
  fieldId: string | null,
): ReadonlyMap<string, number> {
  const counts = fieldId ? valueCounts?.[fieldId] : undefined;
  if (!counts) return new Map();
  return new Map(counts.map((entry) => [entry.value ?? NO_VALUE, entry.count]));
}

/**
 * The filter panel's per-value counts, straight from the server.
 *
 * The panel's client-side counterpart counts the unnarrowed rows, so a chip
 * answers "how many items have this value". Here the numbers come from the same
 * filtered query as the rows, so a chip answers "how many of the items you are
 * looking at have this value" and every number on the board agrees with every
 * other. One query cannot answer both questions, and the one that matches the
 * rows on screen is the one that cannot contradict them.
 */
export function serverFilterValueCounts(
  valueCounts: ServerValueCounts | undefined,
  fieldIds: readonly string[],
): FilterValueCounts {
  return new Map(fieldIds.map((fieldId) => [fieldId, countsForField(valueCounts, fieldId)]));
}

/** Names the panel needs that a field id and a raw value cannot supply. */
export interface RecordLabels {
  field: (field: RecordField) => string;
  /** A stored id as a person's name, or undefined while the directory loads. */
  person: (id: string) => string | undefined;
  /** A workspace id as the workspace's name, never the id itself. */
  workspace: (id: string) => string;
  dateBuckets: Record<DateBucket, string>;
  /** Header for the bucket of rows that have no value for the field. */
  noValue: string;
}

/**
 * Panel columns for a record board, with their values taken from the server's
 * counts rather than from the rows on screen.
 *
 * That is the whole point: the loaded page holds a hundred rows out of
 * thousands, so enumerating its values would offer a filter for whichever
 * statuses happened to sort first. The server counts group over the entire
 * matching set, so every value that exists is offered.
 *
 * A field the server returned no counts for is left out entirely. Offering it
 * with no values would read as "this board has none", which is a different
 * claim from "nothing was counted".
 */
export function buildRecordFilterColumns(
  fields: readonly RecordField[],
  valueCounts: ServerValueCounts | undefined,
  labels: RecordLabels,
): FilterColumnSpec[] {
  return fields.flatMap((field) => {
    if (!field.filter) return [];
    const kind = filterKindForColumnType(field.type) ?? 'plain';

    if (field.filter === 'date') {
      return [
        {
          id: field.id,
          label: labels.field(field),
          kind: 'date' as const,
          options: DATE_BUCKETS.map((bucket) => ({
            value: bucket,
            label: labels.dateBuckets[bucket],
          })),
        },
      ];
    }

    const counted = valueCounts?.[field.id];
    if (!counted?.length) return [];
    const options = counted.flatMap((entry) =>
      entry.value === null
        ? []
        : [{ value: entry.value, label: valueLabel(field, entry.value, labels) }],
    );
    if (options.length === 0) return [];
    return [{ id: field.id, label: labels.field(field), kind, options }];
  });
}

/**
 * How one stored value reads to a person. People and workspace fields carry
 * ids, which only a directory can name; everything else carries the source's
 * own enum, whose snake_case is close enough to a label to title-case in place.
 */
export function valueLabel(field: RecordField, value: string, labels: RecordLabels): string {
  if (field.type === 'people') return labels.person(value) ?? value;
  if (field.id === WORKSPACE_FIELD.id) return labels.workspace(value);
  return humanise(value);
}

/** `in_review` → `In review`. Left alone when it is already prose. */
function humanise(value: string): string {
  if (!/^[a-z0-9]+(?:_[a-z0-9]+)*$/.test(value)) return value;
  const spaced = value.split('_').join(' ');
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

/** `2026-09-02` — the server casts it to the field's own type. */
function isoDay(date: Date): string {
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  const day = `${date.getDate()}`.padStart(2, '0');

  return `${date.getFullYear()}-${month}-${day}`;
}
